'use strict'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { compact, groupBy, uniq } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import UserAction from '../../administration/components/user/actions/UserAction'
import AdministrationAction from '../../administration/actions/AdministrationAction'
import { allLayersProps, LAYERS } from './constants/MapLayersConstants'
import { setLayersOpacity } from './mapTreatments/AddingLayers'
import { Accordion, AccordionDetails, Box, Card, CardContent, Grid, Slider, Stack, Tooltip } from '@mui/material'
import Checkbox from '../forms/Checkbox'
import { parseNowVariableURL } from '../../utils/UrlUtils'
import { getLogin, getSetting, getSettingJson } from '../../utils/SettingUtils'
import { homeListOfLayersOpacity, homeListOfSelectedLayers } from 'carto/constants/CartoConstants'
import AccountAction from 'account/actions/AccountAction'
import moment from 'moment'
import { CardTitle } from 'components/card/NewCard'
import { darkBlue, mainWhite } from 'utils/constants/ColorTheme'
import { AccordionTitle } from 'components/styled/Accordions'
import { createLightColor, RGBToHexColor } from 'utils/ColorUtil'
import { normalizeString } from 'utils/StringUtil'
import { InfoOutlined } from '@mui/icons-material'
import Input from 'components/forms/Input'

const TYPE_PARAMS = ['all', 'showAll', 'alerts', 'territory', 'specificUser']
const SOURCE_SANDRE = [LAYERS.AAC_SANDRE, LAYERS.WATERMASS]

const CartographyControlPanel = ({
    login,
    visible,
    componentType,
    onChangeVisible = () => {},
    layers,
    exists,
    withThemeLayers,
}) => {
    const {
        allcartographyThemes,
        usersThemes,
        applicationSettings,
        settings,
        themeLayers,
    } = useSelector(store => ({
        allcartographyThemes: store.AdministrationReducer.adminThemes,
        usersThemes: store.UserReducer.usersThemes,
        applicationSettings: store.AdministrationReducer.applicationSettings,
        settings: store.AdministrationReducer.userSettings,
        themeLayers: store.HomeReducer.themeLayers,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [layersOpacity, setLayersOpacityState] = useState({})
    const [dataLoaded, setDataLoaded] = useState(false)
    const [layersId, setLayersId] = useState([])
    const [layerSearch, setLayerSearch] = useState('')

    const getSelectedLayersParameter = () => {
        const selectedLayersParameter = settings.find(s => s.parameter === homeListOfSelectedLayers)?.value
        return selectedLayersParameter
    }

    const getCartographyLayers = () => {
        const selectedLayersParameter = getSelectedLayersParameter()
        if (selectedLayersParameter?.length) {
            return selectedLayersParameter
        }
        return ''
    }

    const setLayersState = () => {
        const stringLayers = getCartographyLayers()
        const allLayers = stringLayers.length > 0 ? stringLayers.split(',').map(item => parseInt(item, 10)) : []
        const obj = allLayers.reduce((a, v) => {
            const layerName = themeLayers.find(l => l && l.id === parseInt(v))?.layer
            const opacitySaved = (getSettingJson(settings, homeListOfLayersOpacity) || []).find((s) => s.layerName === layerName)
            const opacity = opacitySaved?.opacity ? opacitySaved.opacity : 1
            setLayersOpacity([{ name: layerName, opacity }])
            return ({ ...a, [layerName]: opacity })
        }, {})
        setLayersOpacityState(obj)
        setDataLoaded(true)
        setLayersId(allLayers)
    }

    useEffect(() => {
        dispatch(AdministrationAction.fetchUserSettings(login ?? getLogin())).then(() => {
            setLayersState()
        })
        setLayersState()

        if (!usersThemes.length) {
            dispatch(UserAction.fetchUsersThemesAssignment())
        }

        dispatch(AdministrationAction.fetchAdminCartographyThemes())
    }, [])

    const saveCartographyLayers = (newLayers = [], isAllChecked) => {
        const allLayers = (newLayers.length <= 1 || isAllChecked) ? layersId.filter(l => !newLayers.includes(l)) : uniq([...newLayers, ...layersId])
        setLayersId(allLayers)
        const filterSettings = settings.filter(o => o.parameter !== homeListOfSelectedLayers)
        const newSettings = [ ...filterSettings, {
            login,
            parameter: homeListOfSelectedLayers,
            updateDate: moment().valueOf(),
            value: allLayers.join() || '',
        }]
        dispatch(AccountAction.updateSettings(login, newSettings))
    }

    const changeVisible = (layerName, id, save = false) => {
        const newValue = !(visible[layerName] || false)
        if (componentType === 'homepage') {
            const obj = { [layerName]: { enabled: newValue } }
            dispatch(AdministrationAction.setSelectedSearchValues('dashboardCheckboxes', obj))
        }
        onChangeVisible([layerName])
        if (layersOpacity[layerName]) {
            setLayersOpacity([{ name: layerName, opacity: layersOpacity[layerName] }])
            setLayersId(prev => [...prev, id])
            if (save) {
                saveCartographyLayers([id])
            }
        } else if (visible[layerName]) {
            setLayersOpacity([{ name: layerName, opacity: 0 }])
            setLayersOpacityState(prev => ({ ...prev, [layerName]: 0 }))
            setLayersId(prev => [...prev, id ])
            if (save) {
                saveCartographyLayers([id])
            }
        } else if (!visible[layerName]) {
            const opacitySaved = (getSettingJson(settings, homeListOfLayersOpacity) || []).find((v) => v.layerName === layerName)
            const opacity = opacitySaved?.opacity ? opacitySaved.opacity : 1
            setLayersOpacity([{ name: layerName, opacity }])
            setLayersOpacityState(prev => ({ ...prev, [layerName]: opacity }))
            setLayersId(prev => [...prev, id ])
            if (save) {
                saveCartographyLayers()
            }
        }
    }

    const getNewOpacity = (layer) => {
        const visibleFound = visible[layer.layer]
        const layerOpacity = layersOpacity[layer.layer]
        if (layerOpacity) {
            return layerOpacity
        } else if (visibleFound) {
            return 0
        }
        const opacitySaved = (getSettingJson(settings, homeListOfLayersOpacity) || []).find((v) => v.layerName === layer.layer)
        return opacitySaved?.opacity ? opacitySaved.opacity : 1
    }

    const matchesLayerSearch = (layer, label = layer.name) => {
        const search = normalizeString(layerSearch)
        return !search || normalizeString(`${label || ''} ${layer.layer || ''}`).includes(search)
    }

    const changeVisibleAll = (groupLayers, isAllChecked) => {
        onChangeVisible(groupLayers.map(l => l.layer), !isAllChecked)
        if (componentType === 'homepage') {
            const newDashboardObj = groupLayers.reduce((acc, l) => ({ ...acc, [l.layer]: { enabled: !(visible[l.layer] || false) } }), {})
            dispatch(AdministrationAction.setSelectedSearchValues('dashboardCheckboxes', newDashboardObj))
        }
        const ids = groupLayers.map(l => l.id)
        const newLayersOpacity = groupLayers.map(l => ({
            ...l,
            opacity: getNewOpacity(l),
        }))
        const newLayersOpacityObj = newLayersOpacity.reduce((acc, l) => ({ ...acc, [l.layer]: l.opacity }), {})
        setLayersId(prev => [...prev, ...ids])
        setLayersOpacityState(prev => ({ ...prev, ...newLayersOpacityObj }))
        setLayersOpacity(newLayersOpacity)
        saveCartographyLayers(ids, isAllChecked)
    }

    const getLegend = (url, layerName) => {
        if (url.includes('#MYMAPS/')) {
            const test = `${parseNowVariableURL(url, getSetting(applicationSettings, 'myMaps'))}&service=WMS&request=GetLegendGraphic&sld_version=1.1.0&format=image/jpeg&layer=${layerName}`
            return test
        }
        const urlLower = url.toLowerCase()
        return `${url}${urlLower.indexOf('?') >= 0 ? urlLower.indexOf('version') >= 0 ? '' : 'version=1.3.0' : '?version=1.3.0'}&service=WMS&request=GetLegendGraphic&sld_version=1.1.0&format=image/jpeg&layer=${layerName}`
    }

    const saveLayersOpacity = (layerName, opacity) => {
        const opacitySettings = getSettingJson(settings, homeListOfLayersOpacity) || []
        const layersOpacityJson = JSON.stringify([
            ...opacitySettings.filter((s) => s.layerName !== layerName),
            { layerName, opacity },
        ])
        const filterSettings = settings.filter(o => o.parameter !== homeListOfLayersOpacity)
        const newSettings = [ ...filterSettings, {
            login,
            parameter: homeListOfLayersOpacity,
            updateDate: moment().valueOf(),
            value: layersOpacityJson,
        }]
        dispatch(AccountAction.updateSettings(login, newSettings))
    }

    const getSwitch = (layer, label) => {
        const { layer: layerName, id } = layer
        const legend = layer.url ? getLegend(layer.url, layerName) : null
        const opacitySaved = (getSettingJson(settings, homeListOfLayersOpacity) || []).find((v) => v.layerName === layerName)
        return (
            <Grid container className='switch label' alignItems='center' sx={{ marginBottom: '20px', color: createLightColor('grey'), cursor: 'pointer' }}>
                <Grid size={legend ? 1 : 2} sx={{ paddingX: '0.75rem' }}>
                    <Checkbox
                        checked={visible[layerName]}
                        onChange={() => changeVisible(layerName, id, true)}
                    />
                </Grid>
                <Grid size={8}>
                    {label}
                </Grid>
                <Grid size={2}>
                    <Slider
                        sliderStyle={{ margin: 0 }}
                        style={{ margin: 0 }}
                        min={0}
                        max={1}
                        step={0.1}
                        value={ layersOpacity[layerName] || (visible[layerName] ? (opacitySaved?.opacity || 1) : 0) }
                        onChange={(event, value) => {
                            setLayersOpacity([{ name: layerName, opacity: value }])
                            setLayersOpacityState(prev => ({ ...prev, [layerName]: value }))
                        }}
                        onChangeCommitted={(e, v) => saveLayersOpacity(layerName, v)}
                    />
                </Grid>
                {legend ? <Grid size={1}>
                    <Tooltip
                        placement='left-start'
                        arrow
                        slotProps={{
                            popper: {
                                disablePortal: true,
                            },
                            tooltip: {
                                sx: {
                                    maxWidth: 'fit-content',
                                    width: 'fit-content',
                                },
                            },
                        }}
                        title={<img src={legend} alt={layerName} />}
                    >
                        <InfoOutlined sx={{ fontSize: '24px' }}/>
                    </Tooltip>
                </Grid> : null }
                {!!layer.source && (
                    <Grid size={11} offset={legend ? 1 : 2}>
                        <i style={{ fontSize: '11px' }}>{i18n.source}: { layer.source }</i>
                    </Grid>
                )}
            </Grid>
        )
    }

    const getThemeLayersSwichs = () => {
        const newLayers = usersThemes.filter(th => th.login === login)
        const themeGroups = groupBy(themeLayers, 'theme')
        if (themeGroups && themeLayers.length) {
            const themeList = allcartographyThemes.filter(theme =>
                (TYPE_PARAMS.includes(componentType) || newLayers.some(th => th.themeId === theme.id && th[componentType]))
                && newLayers.some(th => th.themeId === theme.id && th.visibility),
            )
                .filter(theme => themeGroups[theme.id])
            return themeList.map(theme => {
                const themeGroup = themeGroups[theme.id].filter(layer => matchesLayerSearch(layer))
                if (!themeGroup.length) {
                    return null
                }
                const selectedCount = themeGroup.filter((layer) => !!visible[layer.layer]).length
                const isAllChecked = !themeGroup.some((l) => !visible[l.layer])
                return (
                    <Grid key={theme.id}>
                        <Accordion>
                            <AccordionTitle
                                title={(
                                    <Grid
                                        container
                                        alignItems='center'
                                        justifyContent='space-between'
                                    >
                                        <Grid container>
                                            <Checkbox
                                                checked={isAllChecked}
                                                className='white-checkbox'
                                                onChange={() => {
                                                    changeVisibleAll(themeGroup, isAllChecked)
                                                }}
                                                containerStyle={{ display: 'flex', alignItems: 'center' }}
                                                stopPropagation
                                            />
                                            {theme.name}
                                        </Grid>
                                        {selectedCount > 0 && (
                                            <Box
                                                sx={{
                                                    backgroundColor: mainWhite,
                                                    color: RGBToHexColor(theme.color),
                                                    borderRadius: '999px',
                                                    minWidth: '1.75rem',
                                                    height: '1.75rem',
                                                    px: 0.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '1rem',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {selectedCount}
                                            </Box>
                                        )}
                                    </Grid>
                                )}
                                color={theme.color}
                            />
                            <AccordionDetails>
                                {themeGroup.map(layer => getSwitch(layer, layer.name))}
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                )
            })
        }
        return null
    }

    const getBaseLayers = () => {
        return compact(layers
            .filter(layer => exists[layer] && ![LAYERS.STATIONS_POINTS, LAYERS.STATION, LAYERS.CATCHMENTS].includes(layer))
            .map(layer => {
                const label = allLayersProps({ exists })[layer].name
                return matchesLayerSearch({ layer }, label)
                    ? getSwitch({ layer, source: SOURCE_SANDRE.includes(layer) ? i18n.sandre : '' }, label)
                    : null
            }))
    }

    if (dataLoaded) {
        const baseLayers = getBaseLayers()
        const themeLayersSwichs = withThemeLayers ? getThemeLayersSwichs() : null
        return (
            <Stack spacing={1}>
                <Input
                    title={i18n.search}
                    value={layerSearch}
                    onChange={setLayerSearch}
                    clearFunction
                />
                {baseLayers.length > 0 && (
                    <Card>
                        <CardTitle title={i18n.mapLayers} color={darkBlue} />
                        <CardContent>
                            {baseLayers}
                        </CardContent>
                    </Card>
                ) || (
                    <Card>
                        <CardTitle title={i18n.noLayersProvided} color={darkBlue} />
                    </Card>
                )}

                {themeLayersSwichs}
            </Stack>

        )
    }
    return null
}

CartographyControlPanel.propTypes = {
    componentType: PropTypes.string.isRequired,
    visible: PropTypes.shape({
        DISTRIBUTION_UNIT: PropTypes.bool,
        HYDROMETRIC_STATION: PropTypes.bool,
        INSTALLATION: PropTypes.bool,
        PIEZOMETER: PropTypes.bool,
        PLUVIOMETER: PropTypes.bool,
        PRODUCTION_UNIT: PropTypes.bool,
        QUALITOMETER: PropTypes.bool,
        installationType0: PropTypes.bool,
        installationType1: PropTypes.bool,
        installationType2: PropTypes.bool,
        installationType3: PropTypes.bool,
        installationType4: PropTypes.bool,
        installationType5: PropTypes.bool,
        installationType6: PropTypes.bool,
        installationType7: PropTypes.bool,
        installationType8: PropTypes.bool,
        installationType9: PropTypes.bool,
        installationType10: PropTypes.bool,
        installationType11: PropTypes.bool,
        installationType12: PropTypes.bool,
        installationType13: PropTypes.bool,
        installationType14: PropTypes.bool,
        installationType16: PropTypes.bool,
        installationType17: PropTypes.bool,
        installationType18: PropTypes.bool,
        installationType20: PropTypes.bool,
        'installationType-1': PropTypes.bool,
        qualitometerType0: PropTypes.bool,
        qualitometerType1: PropTypes.bool,
        qualitometerType2: PropTypes.bool,
        qualitometerType3: PropTypes.bool,
        qualitometerType4: PropTypes.bool,
        qualitometerType5: PropTypes.bool,
        qualitometerType6: PropTypes.bool,
        qualitometerType7: PropTypes.bool,
        qualitometerType8: PropTypes.bool,
    }),
    exists: PropTypes.shape({}),
    onChangeVisible: PropTypes.func,
    layers: PropTypes.arrayOf(PropTypes.shape({})),
    withThemeLayers: PropTypes.bool,
    login: PropTypes.string,
}

export default CartographyControlPanel
